import { useCallback } from 'react'
import useCRMStore from '../store/useCRMStore'
import { mergeIntoSnapshot } from '../utils/frozenMerge'

/**
 * Hook que aplica fresh data del backend al snapshot frozen del store.
 *
 * Devuelve una funcion `applyServerUpdate(prev, fresh, currentAgent)` que
 * SIEMPRE retorna `{ contacts, newPhones }`:
 *  - Si frozen.active === false o snapshot vacio: `{ contacts: fresh, newPhones: [] }`
 *    (pass-through). Aplica al boot inicial o cuando el grid quedo sin contacts.
 *  - Si frozen.active === true y snapshot poblado: `{ contacts: visibleMerged, newPhones }`,
 *    donde:
 *      - visibleMerged: solo phones que ya estaban en prev y siguen en fresh, con
 *        datos actualizados in-place. NO incluye nuevos al inicio (eso es decision
 *        del caller). Drop silencioso de phones del snapshot que ya no estan en fresh.
 *      - newPhones: lista de phones detectados como nuevos (en fresh, no en
 *        snapshot/prev, created_at < 24h). El caller decide si auto-insertar al
 *        top (cuando scrollTop ~= 0) o acumular en `pendingNew` del store.
 *  - markCrossAgentDot se sigue dispatchando para reasignaciones.
 *  - markNewRow YA NO se dispatcha aqui: queda a cargo del caller cuando decide
 *    auto-insert (para que el highlight verde solo aparezca cuando la fila se
 *    realmente inserta a la grilla).
 *
 * **Cuidado con `fresh = []`** (respuesta vacia del backend): TODAS las filas
 * del snapshot se dropearian silenciosamente. Si el consumer detecta un error
 * de red transitorio, NO debe llamar applyServerUpdate con `fresh = []`.
 *
 * Cambios INC-034 (2026-05-04): firma cambiada a retorno uniforme {contacts, newPhones}
 * (antes retornaba Array directo en pass-through y array con nuevos prependidos en
 * frozen). Eliminado parametro onNewRowInserted (era para useScrollAnchor que ya no existe).
 *
 * @returns {(prev: Array, fresh: Array, currentAgent: string) => { contacts: Array, newPhones: string[] }}
 */
export function useFrozenMerge() {
  const active = useCRMStore(s => s.frozen.active)
  const snapshot = useCRMStore(s => s.frozen.snapshot)
  const markCrossAgentDot = useCRMStore(s => s.markCrossAgentDot)

  return useCallback((prev, fresh, currentAgent) => {
    if (!active || snapshot.length === 0) {
      return { contacts: fresh, newPhones: [] }
    }

    const { mergedContacts, newPhones, crossAgentDots } = mergeIntoSnapshot(
      snapshot,
      prev,
      fresh,
      currentAgent
    )

    // INC-035 (2026-05-04): NO filtrar mergedContacts por freshPhoneSet.
    // Antes: phones del snapshot que no aparecian en fresh se dropeaban silenciosamente.
    // Esto rompia con paginacion: si el agente cambia el icono/flag de un contacto
    // a un valor que lo mueve fuera del LIMIT del silent re-fetch (ej: 50 primeros
    // por prioridad IC), el backend deja de incluirlo en fresh aunque sigue siendo
    // suyo. Con el filter, el contacto desaparecia visualmente al siguiente refresh
    // post-edicion. Sin el filter, se mantiene visible con sus ultimos datos hasta
    // que el agente descongele y haga loadContacts (donde caera naturalmente si
    // realmente esta out-of-scope).
    //
    // Trade-off aceptable: si otro agente reasigna un contacto del snapshot del usuario
    // mientras el frozen esta activo, sigue visible hasta el descongele. El dot azul
    // cross-agent ya señaliza ese cambio.
    crossAgentDots.forEach(p => markCrossAgentDot(p))

    return { contacts: mergedContacts, newPhones }
  }, [active, snapshot, markCrossAgentDot])
}
